import { Inject, Injectable } from '@angular/core';

import { FirebaseApp, initializeApp } from 'firebase/app';
import { getMessaging, isSupported, Messaging } from 'firebase/messaging';
import {
  Auth,
  browserPopupRedirectResolver,
  inMemoryPersistence,
  initializeAuth,
} from 'firebase/auth';

import {
  AppVariables,
  APP_VARIABLES_TOKEN,
} from '@tablesready/frontend-variables';
import { Capacitor } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class FirebaseService {
  firebaseAuth: Auth;

  private messagingReady: Promise<Messaging | null> = Promise.resolve<Messaging | null>(null);

  constructor(@Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables) {}

  initializeApp(): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    const app = initializeApp({
      apiKey: this.appVariables.FIREBASE_API_KEY,
      authDomain: this.appVariables.FIREBASE_AUTH_DOMAIN,
      projectId: this.appVariables.FIREBASE_PROJECT_ID,
      storageBucket: this.appVariables.FIREBASE_STORAGE_BUCKET,
      messagingSenderId: this.appVariables.FIREBASE_MESSAGING_SENDER_ID,
      appId: this.appVariables.FIREBASE_APP_ID,
    });

    this.firebaseAuth = this.firebaseAuthInitialization(app);
    this.messagingReady = this.firebaseMessagingInitialization(app);
  }

  /**
   * Resolves to the Messaging instance, or null on a browser that cannot
   * receive web push. Never rejects, so callers do not need a catch.
   */
  async getMessaging(): Promise<Messaging | null> {
    return this.messagingReady;
  }

  private firebaseAuthInitialization(app: FirebaseApp): Auth {
    // Not getAuth(): its default indexedDBLocalPersistence polls IndexedDB every
    // 800ms, and Safari drops IndexedDB connections (often around backgrounding),
    // flooding RUM with unhandled "Connection to Indexed Database server lost"
    // rejections. Firebase persistence is unused — the Google ID token is passed to
    // our backend within the current-page login/signup flow and auth.currentUser is
    // never read across page loads — so in-memory avoids all storage polling.
    // initializeAuth() has no default resolver; without browserPopupRedirectResolver
    // signInWithPopup throws auth/argument-error.
    return initializeAuth(app, {
      persistence: inMemoryPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  }

  private async firebaseMessagingInitialization(app: FirebaseApp): Promise<Messaging | null> {
    // getMessaging() throws messaging/unsupported-browser where the Push API,
    // service workers or Notification are missing (iOS Safari outside a home-screen
    // PWA, private windows, older WebViews). Thrown from a constructor it surfaced
    // as an uncaught rejection on every such load. isSupported() answers the same
    // question without throwing; no push simply means no token.
    try {
      if (!(await isSupported())) {
        return null;
      }

      return getMessaging(app);
    } catch {
      // isSupported() already covers every browser-capability case, so reaching
      // here means Firebase itself rejected the config. Push is optional, so the
      // app continues without it; getToken() is never called with a null
      // instance and callers treat a null token as "no push on this device".
      return null;
    }
  }
}
